import { useContext } from "react";
import { type RouteComponentProps } from "react-router";

import { cn } from "@btnet/utilities-core";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { type MenuItemName } from "types/enum";

import { isInPortal } from "utilities/portal/portal";

import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import AccountingBanner from "commonComponents/utilities/HeaderAlerter/AccountingBanner/AccountingBanner";
import AccountingOutageBanner from "commonComponents/utilities/HeaderAlerter/AccountingOutageBanner/AccountingOutageBanner";
import GeneralWarningBanner from "commonComponents/utilities/HeaderAlerter/GeneralWarningBanner/GeneralWarningBanner";
import LeadEmailOverageBanner from "commonComponents/utilities/HeaderAlerter/LeadEmailOverageBanner/LeadEmailOverageBanner";
import MaintenanceBanner from "commonComponents/utilities/HeaderAlerter/MaintenanceBanner/MaintenanceBanner";
import MFAWarningBanner from "commonComponents/utilities/HeaderAlerter/MFAWarningBanner/MFAWarningBanner";
import SubscriptionBanner from "commonComponents/utilities/HeaderAlerter/SubscriptionBanner/SubscriptionBanner";

import "./HeaderAlerter.less";

import { AlertType } from "./HeaderAlerter.types";

interface IHeaderAlerterProps extends RouteComponentProps {
    selectedMenuItem?: MenuItemName;
}

const HeaderAlerterInternal: React.FunctionComponent<IHeaderAlerterProps> = ({
    selectedMenuItem,
    ...routeProps
}) => {
    const userContext = useContext(UserInfoContext);

    return (
        <div
            className={cn("HeaderAlerter", {
                OwnerHeaderAlerter: isInPortal({ owner: true }),
            })}
        >
            {userContext?.systemAlerts.map((alert, index) => (
                <div key={index} className="HeaderAlerter-BannerMargin">
                    {(() => {
                        switch (alert.alertType) {
                            case AlertType.Maintenance:
                                return (
                                    <MaintenanceBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            case AlertType.Subscription:
                                return (
                                    <SubscriptionBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            case AlertType.Accounting:
                                return (
                                    <AccountingBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            case AlertType.AccountingOutage:
                                return (
                                    <AccountingOutageBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            case AlertType.GeneralWarning:
                                return (
                                    <GeneralWarningBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            case AlertType.MFAWarning:
                                return (
                                    <MFAWarningBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            case AlertType.LeadEmailOverage:
                                return (
                                    <LeadEmailOverageBanner
                                        alert={alert}
                                        selectedMenuItem={selectedMenuItem}
                                        {...routeProps}
                                    />
                                );
                            default:
                                return <></>;
                        }
                    })()}
                </div>
            ))}
        </div>
    );
};
const HeaderAlerter = withErrorBoundary(HeaderAlerterInternal)("Could not load Header Alerter");

export default HeaderAlerter;
